import { Component, ReactNode, Suspense, lazy } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Spinner } from '@/components/ui';

/**
 * 图纸查看器的懒加载包装。两端共用。
 *
 * **懒加载的理由**：react-pdf 带着 pdfjs 有一兆多，打卡页根本用不到它。
 * 分出去之后主包小一半，工地上冷启动快的是所有人，
 * 而多花的那一次网络往返只有真的要看图纸的人才付。
 *
 * **错误边界的理由**：懒加载的 chunk 在工地弱网下**是会取不到的**，
 * 而 `React.lazy` 取不到就往上抛 —— 没有边界接住的话整页白屏，
 * 工人看到的是一片空白，连「返回」都没有。改之前手写的 PdfViewer
 * 是 `await import()` 加 try/catch，有这层保护；换成 lazy 不能把它丢了。
 */
const MediaViewer = lazy(() =>
  import('./MediaViewer').then((m) => ({ default: m.MediaViewer })),
);

type Props = Parameters<typeof MediaViewer>[0];

export function LazyMediaViewer(props: Props) {
  return (
    <ViewerBoundary>
      <Suspense fallback={<ViewerLoading />}>
        <MediaViewer {...props} />
      </Suspense>
    </ViewerBoundary>
  );
}

function ViewerLoading() {
  return (
    <div className="flex justify-center py-20 text-ink-3">
      <Spinner />
    </div>
  );
}

/**
 * 取不到 chunk 时给一条能行动的出路：重新加载。
 *
 * 用 `window.location.reload()` 而不是重置 state 重试 —— 浏览器会把失败的
 * 动态 import 结果缓存住，同一个会话里再 import 一次拿到的还是那个失败的
 * Promise，点十次都不会好。
 */
class ViewerBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  override state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  override componentDidCatch(error: unknown) {
    console.warn('[drawings] 查看器加载失败', error);
  }

  override render() {
    if (this.state.failed) return <ViewerFailed />;
    return this.props.children;
  }
}

function ViewerFailed() {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-start gap-3 py-6">
      <Alert>{t('drawings.loadFailed')}</Alert>
      <Button variant="secondary" onClick={() => window.location.reload()}>
        {t('app.retry')}
      </Button>
    </div>
  );
}
